/**
 * Painel administrativo.
 *
 * Observabilidade de uso e custo por pessoa (em R$). Lista os usuários com uso
 * agregado, permite abrir o detalhe de cada um (uso diário dos últimos 30 dias +
 * custo por modelo) e ajustar a cotação USD→BRL usada no cálculo de custo.
 *
 * Guardada pelo `beforeLoad` da rota (exige o claim `admin`). Exportada como
 * `AdminRoute`. Todo o texto é pt-BR; sem emoji.
 *
 * A série de 30 dias vem diretamente de `adminGetUser` (campo `usageDaily`) — não
 * há hook de snapshot em tempo real, pois aqui o admin consulta o histórico de
 * QUALQUER usuário, não o seu próprio.
 */
import type {
  AdminGetUserResponse,
  AdminUserSummary,
  UsageDailyDoc,
  UsageFeature,
  UsageModelBreakdown,
  UserFeatureFlags,
} from '@agente/shared'
import { Loader, Search, ShieldCheck } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  adminGetPricing,
  adminGetUser,
  adminListUsers,
  adminSetUserFeatures,
  adminUpdatePricing,
} from '@/lib/callables'
import { formatCurrencyBRL } from '@/lib/format'

/** Rótulos pt-BR das features gateadas por usuário (painel admin). */
const FEATURE_TOGGLES: Array<{ key: keyof UserFeatureFlags; label: string }> = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'googleAccount', label: 'Conta Google' },
  { key: 'msExtraAccounts', label: 'Contas Microsoft adicionais' },
  { key: 'whatsappOutbound', label: 'WhatsApp para contatos (disparo)' },
]

const PAGE_SIZE = 50

const numberFmt = new Intl.NumberFormat('pt-BR')

/** "12.345" — inteiro no padrão pt-BR. */
function formatInt(value: number): string {
  return numberFmt.format(Number.isFinite(value) ? value : 0)
}

/** Data curta a partir de um id YYYY-MM-DD (mostra apenas MM-DD). */
function shortDate(date: string): string {
  return date.length >= 10 ? date.slice(5) : date
}

type AccessFilter = 'all' | 'pending'

interface ModelCostRow {
  model: string
  inputTokens: number
  outputTokens: number
  costBRL: number
}

/** Agrega o campo `byModel` de todos os dias de uso em uma linha por modelo. */
function aggregateByModel(usageDaily: UsageDailyDoc[]): ModelCostRow[] {
  const acc = new Map<string, ModelCostRow>()
  for (const day of usageDaily) {
    for (const [model, m] of Object.entries(day.byModel ?? {})) {
      const row = acc.get(model) ?? {
        model,
        inputTokens: 0,
        outputTokens: 0,
        costBRL: 0,
      }
      row.inputTokens += m.inputTokens
      row.outputTokens += m.outputTokens
      row.costBRL += m.costBRL
      acc.set(model, row)
    }
  }
  return Array.from(acc.values()).sort((a, b) => b.costBRL - a.costBRL)
}

interface FeatureCostRow {
  key: string
  label: string
  inputTokens: number
  outputTokens: number
  costBRL: number
}

/**
 * Agrupamento pt-BR do `UsageFeature` granular em linhas do detalhamento por
 * recurso. Lembretes soma `reminderMessage` (lembrete comum) + `emailReminders`
 * (lembrete entregue por e-mail) numa única linha — do ponto de vista do usuário
 * é o mesmo recurso, só muda o canal de entrega.
 */
const FEATURE_GROUPS: { key: string; label: string; features: UsageFeature[] }[] = [
  { key: 'chat', label: 'Chat', features: ['chat'] },
  { key: 'reminders', label: 'Lembretes', features: ['reminderMessage', 'emailReminders'] },
  { key: 'voice', label: 'Voz', features: ['voice'] },
  { key: 'briefing', label: 'Resumos', features: ['briefing'] },
  { key: 'webSearch', label: 'Pesquisa web', features: ['webSearch'] },
  { key: 'memory', label: 'Memória', features: ['memoryExtraction'] },
  { key: 'emailResearch', label: 'Pesquisa de e-mail', features: ['emailResearch'] },
  { key: 'whatsapp', label: 'WhatsApp', features: ['whatsappMedia'] },
]

/**
 * Agrega o campo `byFeature` de todos os dias de uso por grupo pt-BR (ver
 * `FEATURE_GROUPS`), ordenado por custo desc e ocultando grupos zerados.
 */
function aggregateByFeature(usageDaily: UsageDailyDoc[]): FeatureCostRow[] {
  const totals = new Map<UsageFeature, UsageModelBreakdown>()
  for (const day of usageDaily) {
    for (const [feature, m] of Object.entries(day.byFeature ?? {}) as [
      UsageFeature,
      UsageModelBreakdown,
    ][]) {
      const row = totals.get(feature) ?? { inputTokens: 0, outputTokens: 0, costBRL: 0 }
      row.inputTokens += m.inputTokens
      row.outputTokens += m.outputTokens
      row.costBRL += m.costBRL
      totals.set(feature, row)
    }
  }
  return FEATURE_GROUPS.map((group) => {
    const acc = { inputTokens: 0, outputTokens: 0, costBRL: 0 }
    for (const feature of group.features) {
      const m = totals.get(feature)
      if (!m) continue
      acc.inputTokens += m.inputTokens
      acc.outputTokens += m.outputTokens
      acc.costBRL += m.costBRL
    }
    return { key: group.key, label: group.label, ...acc }
  })
    .filter((row) => row.inputTokens > 0 || row.outputTokens > 0 || row.costBRL > 0)
    .sort((a, b) => b.costBRL - a.costBRL)
}

/** Série de custo diário (R$) dos últimos 30 dias, ordenada por data. */
function costSeries(usageDaily: UsageDailyDoc[]): { date: string; custo: number }[] {
  return [...usageDaily]
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-30)
    .map((d) => ({ date: d.date, custo: d.totalCostBRL }))
}

export function AdminRoute() {
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [aggregate, setAggregate] = useState<{
    totalUsers: number
    totalInputTokens: number
    totalOutputTokens: number
    totalCostBRL: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | undefined>()

  const [search, setSearch] = useState('')
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all')

  const [selectedUid, setSelectedUid] = useState<string | null>(null)
  const [userDetail, setUserDetail] = useState<AdminGetUserResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [savingFeature, setSavingFeature] = useState<keyof UserFeatureFlags | null>(null)

  const [usdToBrlRate, setUsdToBrlRate] = useState('')
  const [pricingLoading, setPricingLoading] = useState(true)
  const [savingRate, setSavingRate] = useState(false)

  const fetchUsers = useCallback((cursor?: string) => {
    if (cursor) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }
    adminListUsers({ limit: PAGE_SIZE, cursor })
      .then(({ data }) => {
        setUsers((prev) => (cursor ? [...prev, ...data.users] : data.users))
        setAggregate(data.aggregate)
        setNextCursor(data.nextCursor)
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Tente novamente.'
        toast.error('Não foi possível carregar os usuários', { description: msg })
      })
      .finally(() => {
        setLoading(false)
        setLoadingMore(false)
      })
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    setPricingLoading(true)
    adminGetPricing()
      .then(({ data }) => {
        setUsdToBrlRate(String(data.usdToBrlRate))
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Tente novamente.'
        toast.error('Não foi possível carregar a cotação', { description: msg })
      })
      .finally(() => setPricingLoading(false))
  }, [])

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    return users.filter((u) => {
      if (accessFilter === 'pending' && u.allowed) return false
      if (q) {
        const inEmail = u.email.toLowerCase().includes(q)
        const inName = (u.displayName ?? '').toLowerCase().includes(q)
        if (!inEmail && !inName) return false
      }
      return true
    })
  }, [users, search, accessFilter])

  async function handleSelect(uid: string) {
    setSelectedUid(uid)
    setUserDetail(null)
    setDetailLoading(true)
    try {
      const { data } = await adminGetUser({ uid })
      setUserDetail(data)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível carregar o detalhe', { description: msg })
    } finally {
      setDetailLoading(false)
    }
  }

  async function handleToggleFeature(feature: keyof UserFeatureFlags, enabled: boolean) {
    if (!selectedUid) return
    setSavingFeature(feature)
    try {
      const { data } = await adminSetUserFeatures({
        uid: selectedUid,
        features: { [feature]: enabled },
      })
      setUserDetail((prev) =>
        prev ? { ...prev, user: { ...prev.user, features: data.features } } : prev,
      )
      // Reflete também na linha da tabela, sem precisar recarregar a página inteira.
      setUsers((prev) =>
        prev.map((u) => (u.uid === selectedUid ? { ...u, features: data.features } : u)),
      )
      toast.success('Feature atualizada')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível atualizar a feature', { description: msg })
    } finally {
      setSavingFeature(null)
    }
  }

  async function handleSaveRate() {
    const parsed = Number(usdToBrlRate.replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error('Informe uma cotação válida', { description: 'Use um número maior que zero.' })
      return
    }
    setSavingRate(true)
    try {
      await adminUpdatePricing({ usdToBrlRate: parsed })
      toast.success('Cotação atualizada')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível salvar a cotação', { description: msg })
    } finally {
      setSavingRate(false)
    }
  }

  const modelRows = useMemo(
    () => (userDetail ? aggregateByModel(userDetail.usageDaily) : []),
    [userDetail],
  )
  const featureRows = useMemo(
    () => (userDetail ? aggregateByFeature(userDetail.usageDaily) : []),
    [userDetail],
  )
  const series = useMemo(() => (userDetail ? costSeries(userDetail.usageDaily) : []), [userDetail])

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="text-primary size-7" />
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Painel admin</h1>
          <p className="text-muted-foreground text-sm">Uso e custo por pessoa</p>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">
              Total de usuários
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !aggregate ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-semibold">{formatInt(aggregate?.totalUsers ?? 0)}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">
              Custo total do período
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !aggregate ? (
              <Skeleton className="h-8 w-28" />
            ) : (
              <div className="text-2xl font-semibold">
                {formatCurrencyBRL(aggregate?.totalCostBRL ?? 0)}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">
              Tokens (entrada / saída)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !aggregate ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <div className="text-2xl font-semibold">
                {formatInt(aggregate?.totalInputTokens ?? 0)}
                <span className="text-muted-foreground mx-1 text-lg font-normal">/</span>
                {formatInt(aggregate?.totalOutputTokens ?? 0)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="text-muted-foreground pointer-events-none absolute top-2.5 left-2.5 size-4" />
          <Input
            placeholder="Buscar por nome ou e-mail"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar usuários"
          />
        </div>
        <Select value={accessFilter} onValueChange={(v) => setAccessFilter(v as AccessFilter)}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="Filtrar por acesso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendentes de acesso</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <Card className="overflow-hidden py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Acesso</TableHead>
              <TableHead className="text-right">Tokens entrada</TableHead>
              <TableHead className="text-right">Tokens saída</TableHead>
              <TableHead className="text-right">Custo (R$)</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton estático
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-10 text-center">
                  Nenhum usuário encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((u) => (
                <TableRow key={u.uid}>
                  <TableCell>
                    <div className="font-medium">{u.displayName || 'Sem nome'}</div>
                    <div className="text-muted-foreground text-xs">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    {u.allowed ? (
                      <span className="bg-primary/10 text-primary inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium">
                        Liberado
                      </span>
                    ) : (
                      <span className="bg-muted text-muted-foreground inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium">
                        Pendente
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(u.usage?.totalInputTokens ?? 0)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(u.usage?.totalOutputTokens ?? 0)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrencyBRL(u.usage?.totalCostBRL ?? 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleSelect(u.uid)}>
                      Detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {nextCursor ? (
          <div className="border-border flex justify-center border-t p-3">
            <Button variant="outline" disabled={loadingMore} onClick={() => fetchUsers(nextCursor)}>
              {loadingMore ? <Loader className="size-4 animate-spin" /> : null}
              Carregar mais
            </Button>
          </div>
        ) : null}
      </Card>

      {/* Cotação USD → BRL */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cotação de câmbio (USD → BRL)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="usd-brl-rate">Reais por dólar</Label>
              {pricingLoading ? (
                <Skeleton className="h-9 w-40" />
              ) : (
                <Input
                  id="usd-brl-rate"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  className="w-40"
                  value={usdToBrlRate}
                  onChange={(e) => setUsdToBrlRate(e.target.value)}
                />
              )}
            </div>
            <Button onClick={handleSaveRate} disabled={savingRate || pricingLoading}>
              {savingRate ? <Loader className="size-4 animate-spin" /> : null}
              Salvar
            </Button>
          </div>
          <p className="text-muted-foreground mt-3 text-xs">
            Usada para converter o custo em dólares dos modelos para reais.
          </p>
        </CardContent>
      </Card>

      <p className="text-muted-foreground text-xs">
        Valores são estimativa (tokens x tabela de preços) — não substituem a fatura oficial do GCP.
      </p>

      {/* Detalhe do usuário */}
      <Sheet open={selectedUid !== null} onOpenChange={(open) => !open && setSelectedUid(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Detalhes do usuário</SheetTitle>
            <SheetDescription>Uso diário e custo por modelo.</SheetDescription>
          </SheetHeader>

          <div className="space-y-6 px-4 pb-6">
            {detailLoading || !userDetail ? (
              <div className="space-y-4">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-muted-foreground text-xs font-medium">Nome</div>
                    <div className="text-sm">{userDetail.user.displayName || 'Sem nome'}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-muted-foreground text-xs font-medium">E-mail</div>
                    <div className="text-sm break-all">{userDetail.user.email}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-muted-foreground text-xs font-medium">Acesso</div>
                    <div className="text-sm">{userDetail.allowed ? 'Liberado' : 'Pendente'}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-muted-foreground text-xs font-medium">Administrador</div>
                    <div className="text-sm">{userDetail.admin ? 'Sim' : 'Não'}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">Features</h3>
                  <p className="text-muted-foreground text-xs">
                    Liberação gradual de recursos. Desligar não desfaz conexões já feitas — só
                    bloqueia novas (WhatsApp e WhatsApp para contatos são a exceção: o ENVIO some
                    imediatamente ao desligar, mesmo com número já vinculado).
                  </p>
                  <div className="flex flex-col gap-3">
                    {FEATURE_TOGGLES.map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between gap-4">
                        <Label htmlFor={`feature-${key}`} className="text-sm font-normal">
                          {label}
                        </Label>
                        <div className="flex items-center gap-2">
                          {savingFeature === key ? (
                            <Loader className="text-muted-foreground size-4 animate-spin" />
                          ) : null}
                          <Switch
                            id={`feature-${key}`}
                            checked={userDetail.user.features?.[key] === true}
                            disabled={savingFeature !== null}
                            onCheckedChange={(checked) => void handleToggleFeature(key, checked)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">Custo por dia (últimos 30 dias)</h3>
                  <div className="h-64 w-full">
                    {series.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={series}>
                          <XAxis
                            dataKey="date"
                            fontSize={11}
                            tickFormatter={shortDate}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            fontSize={11}
                            width={40}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v: number) => formatCurrencyBRL(v)}
                          />
                          <Tooltip
                            cursor={{ fill: 'var(--muted)' }}
                            contentStyle={{
                              backgroundColor: 'var(--popover)',
                              borderRadius: '8px',
                              border: '1px solid var(--border)',
                              color: 'var(--popover-foreground)',
                              fontSize: '12px',
                            }}
                            formatter={(v) => [formatCurrencyBRL(Number(v) || 0), 'Custo']}
                          />
                          <Bar dataKey="custo" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-muted-foreground flex h-full items-center justify-center rounded-md border text-sm">
                        Sem dados de uso neste período.
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">Custo por modelo</h3>
                  {modelRows.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Modelo</TableHead>
                          <TableHead className="text-right">Entrada</TableHead>
                          <TableHead className="text-right">Saída</TableHead>
                          <TableHead className="text-right">Custo (R$)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {modelRows.map((m) => (
                          <TableRow key={m.model}>
                            <TableCell className="font-medium">{m.model}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatInt(m.inputTokens)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatInt(m.outputTokens)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrencyBRL(m.costBRL)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">Sem consumo por modelo.</p>
                  )}
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">Custo por recurso</h3>
                  {featureRows.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Recurso</TableHead>
                          <TableHead className="text-right">Entrada</TableHead>
                          <TableHead className="text-right">Saída</TableHead>
                          <TableHead className="text-right">Custo (R$)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {featureRows.map((f) => (
                          <TableRow key={f.key}>
                            <TableCell className="font-medium">{f.label}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatInt(f.inputTokens)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatInt(f.outputTokens)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrencyBRL(f.costBRL)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">Sem consumo por recurso.</p>
                  )}
                  <p className="text-muted-foreground text-xs">
                    Voz é estimativa por duração da sessão (segundos de áudio), não por tokens —
                    calibrável ajustando o preço do recurso na configuração.
                  </p>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default AdminRoute
